/**
 * customer-account.js
 *
 * All custom elements for classic customer account pages.
 * Idiom mirrors assets/cart-drawer.js — each element guarded by customElements.get().
 *
 * Elements defined here:
 *   <customer-password-field>   — eye-toggle, re-dispatches input for downstream listeners
 *   <customer-password-rules>   — live 5-rule validator, emits customer:password-valid
 *   <customer-password-confirm> — match-check between two fields, emits customer:password-match
 *   <customer-register-form>    — enables submit when all validations pass
 *   <customer-login-form>       — sign-in / recover panel switcher + #recover hash deep-link
 *   <customer-privacy-toggle>   — aria-expanded toggle for privacy notice
 *   <customer-address-form>     — hand-rolled Canadian province list + pre-selection
 *   <customer-account-dialogs>  — document singleton: delegates [data-customer-dialog-open/close]
 */

// ---------------------------------------------------------------------------
// Password validation regexes (plan spec)
// ---------------------------------------------------------------------------
const PASSWORD_RULES = {
  'min-char': (v) => v.length >= 12 && v.length <= 40, // 12–40 chars, matches production policy
  upper: (v) => /[A-Z]/.test(v),
  lower: (v) => /[a-z]/.test(v),
  number: (v) => /\d/.test(v),
  special: (v) => /[^A-Za-z0-9]/.test(v),
};

/**
 * something@something.tld, where the TLD is at least two characters.
 *
 * The two-character floor is the real-world minimum: the root zone has no
 * single-character TLDs, the shortest being the two-letter country codes (.ca,
 * .co, .uk). Without it this accepted `a@b.c`.
 *
 * The last label is deliberately `[^\s@.]{2,}` rather than `[a-z]{2,}`. Letters
 * only would reject punycode TLDs such as `xn--p1ai`, which carry digits and
 * hyphens. On a signup form a false rejection locks a real customer out, which
 * costs more than letting an implausible address like `a@b.99` through to
 * Shopify — so this errs toward accepting.
 */
const ACCOUNT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/;

/**
 * One definition of "valid" shared by the sign-in and create-account forms.
 *
 * Native email validation is not enough on its own: the HTML spec does not require
 * a dot in the domain, so `input.validity.valid` is true for `john@john`. The two
 * forms had drifted apart on this — sign-in applied ACCOUNT_EMAIL_REGEX,
 * create-account trusted native validity and so accepted a TLD-less address.
 *
 * Trusting native validity for everything else keeps constraints like maxlength
 * enforced rather than reimplemented here.
 *
 * @param {HTMLInputElement} input
 * @returns {boolean}
 */
function isAccountInputValid(input) {
  const value = input.value.trim();
  if (input.required && value.length === 0) return false;

  if (input.type === 'email') {
    // Both checks, because each catches what the other misses. Native rejects
    // malformed local and domain parts (`john@john.c`1`) but accepts a missing TLD
    // (`john@john`); the regex is the other way round. Trusting only the regex left
    // the first case to be reported by the browser in its own words.
    const nativeOk = input.validity ? input.validity.valid : true;
    return nativeOk && ACCOUNT_EMAIL_REGEX.test(value);
  }

  return input.validity ? input.validity.valid : true;
}

/**
 * Resolve the message for an invalid account field. Cannot lean on
 * `input.validationMessage` for a bad email, because native validity considers
 * `john@john` fine and leaves that string empty — which rendered a blank error.
 *
 * @param {HTMLInputElement} input
 * @param {DOMStringMap} strings - dataset carrying the localised overrides
 */
function getAccountInputErrorMessage(input, strings) {
  const value = input.value.trim();

  if (input.type === 'email') {
    // Deliberately no fall-through to input.validationMessage for email. That is the
    // browser's own string: untranslated, and phrased at the level of the parser
    // rather than the shopper — "A part following '@' should not contain the symbol
    // '`'". One piece of site copy covers every way an address can be malformed,
    // and it is the copy that exists in both EN and FR.
    return value.length === 0 ? strings.errorBlankEmail || '' : strings.errorInvalidEmail || '';
  }

  if (input.type === 'password' && value.length === 0) {
    return strings.errorBlankPassword || input.validationMessage;
  }

  return input.validationMessage;
}

// ---------------------------------------------------------------------------
// Canadian provinces (hand-rolled — no Shopify.CountryProvinceSelector needed)
// ---------------------------------------------------------------------------
const CA_PROVINCES = [
  { code: 'AB', label: 'Alberta' },
  { code: 'BC', label: 'British Columbia' },
  { code: 'MB', label: 'Manitoba' },
  { code: 'NB', label: 'New Brunswick' },
  { code: 'NL', label: 'Newfoundland and Labrador' },
  { code: 'NS', label: 'Nova Scotia' },
  { code: 'NT', label: 'Northwest Territories' },
  { code: 'NU', label: 'Nunavut' },
  { code: 'ON', label: 'Ontario' },
  { code: 'PE', label: 'Prince Edward Island' },
  { code: 'QC', label: 'Quebec' },
  { code: 'SK', label: 'Saskatchewan' },
  { code: 'YT', label: 'Yukon' },
];

// ---------------------------------------------------------------------------
// <customer-password-field>
// Eye-toggle: swaps input type text/password and swaps icon visibility.
// Re-dispatches an 'input' event so upstream listeners (rules, confirm) fire.
// ---------------------------------------------------------------------------
if (!customElements.get('customer-password-field')) {
  class CustomerPasswordField extends HTMLElement {
    connectedCallback() {
      this.#toggle = this.querySelector('.customer-password-field__toggle');
      this.#input = this.querySelector('.customer-password-field__input');
      this.#iconShow = this.querySelector('.customer-password-field__icon--show');
      this.#iconHide = this.querySelector('.customer-password-field__icon--hide');

      this.#toggle?.addEventListener('click', this.#handleToggle);
    }

    disconnectedCallback() {
      this.#toggle?.removeEventListener('click', this.#handleToggle);
    }

    /** @type {HTMLButtonElement | null} */
    #toggle = null;
    /** @type {HTMLInputElement | null} */
    #input = null;
    /** @type {Element | null} */
    #iconShow = null;
    /** @type {Element | null} */
    #iconHide = null;

    #handleToggle = () => {
      if (!this.#input || !this.#toggle) return;

      const isPassword = this.#input.type === 'password';
      this.#input.type = isPassword ? 'text' : 'password';

      const showLabel = this.#toggle.dataset.labelShow ?? '';
      const hideLabel = this.#toggle.dataset.labelHide ?? '';
      this.#toggle.setAttribute('aria-label', isPassword ? hideLabel : showLabel);
      this.#toggle.setAttribute('aria-pressed', String(isPassword));

      // Swap icons
      if (this.#iconShow) this.#iconShow.toggleAttribute('hidden', isPassword);
      if (this.#iconHide) this.#iconHide.toggleAttribute('hidden', !isPassword);

      // Re-dispatch input so rules/confirm elements re-evaluate
      this.#input.dispatchEvent(new Event('input', { bubbles: true }));
    };
  }

  customElements.define('customer-password-field', CustomerPasswordField);
}

// ---------------------------------------------------------------------------
// <customer-password-rules>
// Watches the target input (data-password-rules-for="<id>").
// Tests each rule's regex, toggles .is-valid on the <li>.
// Emits 'customer:password-valid' with { allValid: boolean } when state changes.
// ---------------------------------------------------------------------------
if (!customElements.get('customer-password-rules')) {
  class CustomerPasswordRules extends HTMLElement {
    connectedCallback() {
      // The list element carries the target id
      this.#list = this.querySelector('[data-password-rules-for]');
      if (!this.#list) return;

      const targetId = this.#list.dataset.passwordRulesFor;
      this.#input = document.getElementById(targetId ?? '');
      if (!this.#input) return;

      this.#items = [...this.#list.querySelectorAll('[data-rule]')];
      this.#input.addEventListener('input', this.#evaluate);
      // Run once for pre-filled values
      this.#evaluate();
    }

    disconnectedCallback() {
      this.#input?.removeEventListener('input', this.#evaluate);
    }

    /** @type {HTMLElement | null} */
    #list = null;
    /** @type {HTMLElement | null} */
    #input = null;
    /** @type {HTMLElement[]} */
    #items = [];
    #previousAllValid = null;

    /**
     * Public boolean: true when all 5 rules pass.
     * CustomerPasswordConfirm reads this to gate its submit button.
     * @type {boolean}
     */
    allValid = false;

    #evaluate = () => {
      const value = /** @type {HTMLInputElement} */ (this.#input)?.value ?? '';
      let allValid = true;

      for (const item of this.#items) {
        const rule = item.dataset.rule ?? '';
        const fn = PASSWORD_RULES[rule];
        const valid = fn ? fn(value) : false;

        item.classList.toggle('is-valid', valid);
        const status = valid ? this.#list.dataset.validLabel : this.#list.dataset.invalidLabel;
        if (status) item.setAttribute('aria-label', `${item.textContent.trim()} — ${status}`);
        if (!valid) allValid = false;
      }

      // Update public property every evaluation so CustomerPasswordConfirm can read it
      this.allValid = allValid;

      // Only emit when state changes to avoid spam.
      // bubbles: true + composed: true so it crosses shadow DOM and reaches
      // ancestor listeners (CustomerPasswordConfirm, CustomerRegisterForm).
      if (allValid !== this.#previousAllValid) {
        this.#previousAllValid = allValid;
        this.dispatchEvent(
          new CustomEvent('customer:password-valid', {
            detail: { allValid },
            bubbles: true,
            composed: true,
          })
        );
      }
    };
  }

  customElements.define('customer-password-rules', CustomerPasswordRules);
}

// ---------------------------------------------------------------------------
// <customer-password-confirm>
// Compares the confirm input against the source field (data-compare-pw="#<id>").
// Toggles aria-invalid on the confirm input.
// Emits 'customer:password-match' with { match: boolean }.
//
// Also gates the nearest submit button (excluding name="decline") so the form
// cannot be submitted until passwords (a) match AND (b) rules all pass.
// It subscribes to the bubbling customer:password-valid event from the form
// to track the rules state without needing a direct reference to the rules element.
// ---------------------------------------------------------------------------
if (!customElements.get('customer-password-confirm')) {
  class CustomerPasswordConfirm extends HTMLElement {
    connectedCallback() {
      // The confirm input lives directly inside this element
      this.#confirmInput = this.querySelector('input[data-compare-pw]');
      if (!this.#confirmInput) return;

      const sourceSelector = this.#confirmInput.dataset.comparePw ?? '';
      this.#sourceInput = document.querySelector(sourceSelector);

      // Cache the parent form so disconnectedCallback can cleanly remove listeners.
      this.#form = this.closest('form');

      // Locate the submit button (exclude the Decline button on activate-account
      // which must never be disabled).
      this.#submitButton = this.#form?.querySelector(
        'button[type="submit"]:not([name="decline"])'
      ) ?? null;

      // Disable the submit immediately — enabled once both conditions are met.
      if (this.#submitButton) {
        this.#submitButton.disabled = true;
        this.#submitButton.setAttribute('aria-disabled', 'true');
      }

      this.#error = this.#ensureErrorElement();
      this.#confirmInput.addEventListener('input', this.#evaluate);
      this.#sourceInput?.addEventListener('input', this.#evaluate);

      // Listen for the bubbling rules event from CustomerPasswordRules inside the
      // same form. Cache the last allValid value to combine with the match check.
      this.#form?.addEventListener('customer:password-valid', this.#handlePasswordValid);
    }

    disconnectedCallback() {
      this.#confirmInput?.removeEventListener('input', this.#evaluate);
      this.#sourceInput?.removeEventListener('input', this.#evaluate);
      this.#form?.removeEventListener('customer:password-valid', this.#handlePasswordValid);
      this.#form = null;
    }

    /** @type {HTMLInputElement | null} */
    #confirmInput = null;
    /** @type {HTMLInputElement | null} */
    #sourceInput = null;
    /** @type {HTMLButtonElement | null} */
    #submitButton = null;
    /** Cached parent form — stored so disconnectedCallback can remove the listener safely */
    #form = null;
    /** @type {HTMLElement | null} */
    #error = null;
    /** Tracks the latest allValid state reported by CustomerPasswordRules */
    #rulesAllValid = false;

    #handlePasswordValid = (event) => {
      this.#rulesAllValid = event.detail?.allValid ?? false;
      this.#updateSubmit();
    };

    #evaluate = () => {
      const source = this.#sourceInput?.value ?? '';
      const confirm = this.#confirmInput?.value ?? '';

      // Only mark invalid if both fields have content
      const match = source === confirm;
      const showInvalid = !match && confirm.length > 0;

      this.#confirmInput?.setAttribute('aria-invalid', showInvalid ? 'true' : 'false');
      this.#confirmInput?.classList.toggle('customer-password-field__input--invalid', showInvalid);
      this.#updateError(showInvalid);

      this.dispatchEvent(
        new CustomEvent('customer:password-match', {
          detail: { match },
          bubbles: true,
        })
      );

      this.#updateSubmit();
    };

    #ensureErrorElement() {
      if (!this.#confirmInput) return null;

      const existingErrorId = `${this.#confirmInput.id}-error`;
      let error = document.getElementById(existingErrorId);
      if (!error) {
        error = document.createElement('p');
        error.id = existingErrorId;
        error.className = 'customer-password-field__error';
        error.setAttribute('role', 'alert');
        error.hidden = true;
        this.append(error);
      }

      this.#addDescribedBy(this.#confirmInput, existingErrorId);
      return error;
    }

    #updateError(showInvalid) {
      if (!this.#error || !this.#confirmInput) return;

      this.#error.textContent = showInvalid ? this.dataset.errorMismatch || '' : '';
      this.#error.hidden = !showInvalid;
    }

    #addDescribedBy(input, id) {
      const ids = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (!ids.includes(id)) {
        ids.push(id);
        input.setAttribute('aria-describedby', ids.join(' '));
      }
    }

    #updateSubmit() {
      if (!this.#submitButton) return;

      const source = this.#sourceInput?.value ?? '';
      const confirm = this.#confirmInput?.value ?? '';
      const match = source === confirm && confirm.length > 0;

      // Both conditions must hold: rules pass AND passwords match
      const disabled = !(this.#rulesAllValid && match);
      this.#submitButton.disabled = disabled;
      this.#submitButton.setAttribute('aria-disabled', String(disabled));
    }
  }

  customElements.define('customer-password-confirm', CustomerPasswordConfirm);
}

// ---------------------------------------------------------------------------
// <customer-register-form>
// Listens for customer:password-valid; also watches required text/email fields.
// Enables the submit button only when all conditions are satisfied.
// ---------------------------------------------------------------------------
if (!customElements.get('customer-register-form')) {
  class CustomerRegisterForm extends HTMLElement {
    connectedCallback() {
      this.#form = this.querySelector('form');
      this.#submit = this.querySelector('[type="submit"]');
      this.#errorSummary = this.querySelector('[data-register-error-summary]');
      this.#errorText = this.querySelector('[data-register-error-text]');
      this.#serverEmailError = this.querySelector('#customer-register-email-error');
      if (this.#submit) {
        // Disable initially; will be re-enabled when valid
        this.#submit.disabled = true;
        this.#submit.setAttribute('aria-disabled', 'true');
      }

      // Required text/email inputs (excluding password — rules cover that)
      this.#requiredInputs = [
        ...this.querySelectorAll('input[required]:not([type="password"])'),
      ];

      this.addEventListener('customer:password-valid', this.#handlePasswordValid);
      this.#form?.addEventListener('submit', this.#handleSubmit);

      for (const input of this.#requiredInputs) {
        input.addEventListener('input', this.#handleRequiredInput);
        input.addEventListener('invalid', this.#handleInvalid);
        input.addEventListener('blur', this.#handleRequiredBlur);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('customer:password-valid', this.#handlePasswordValid);
      this.#form?.removeEventListener('submit', this.#handleSubmit);
      for (const input of this.#requiredInputs) {
        input.removeEventListener('input', this.#handleRequiredInput);
        input.removeEventListener('invalid', this.#handleInvalid);
        input.removeEventListener('blur', this.#handleRequiredBlur);
      }
    }

    /** @type {HTMLFormElement | null} */
    #form = null;
    /** @type {HTMLButtonElement | null} */
    #submit = null;
    /** @type {HTMLElement | null} */
    #errorSummary = null;
    /** @type {HTMLElement | null} */
    #errorText = null;
    /** @type {HTMLInputElement[]} */
    #requiredInputs = [];
    #passwordAllValid = false;
    /**
     * The input the visible summary is currently describing. The summary shows one
     * message at a time, so this is what decides when it can be cleared.
     * @type {HTMLInputElement | null}
     */
    #errorInput = null;
    /**
     * The server-rendered email message, present only after a rejected submission.
     * It shares a field group with the client summary, so the two must never show
     * together. See the comment beside it in main-customer-register.liquid.
     * @type {HTMLElement | null}
     */
    #serverEmailError = null;

    #handlePasswordValid = (event) => {
      this.#passwordAllValid = event.detail?.allValid ?? false;
      this.#evaluate();
    };

    /**
     * Retire the server-rendered email message. Called on the two occasions it stops
     * being the truth: the shopper edits the field, so it describes a value that no
     * longer exists, or the client has its own message for the same field.
     */
    #dismissServerEmailError() {
      if (this.#serverEmailError) this.#serverEmailError.hidden = true;
    }

    #handleRequiredInput = (event) => {
      const input = /** @type {HTMLInputElement} */ (event.currentTarget);

      // Before the validity check, not inside it: the message is stale the moment
      // the value changes, whether the new value is any better or not.
      if (input.type === 'email') this.#dismissServerEmailError();

      if (isAccountInputValid(input)) {
        input.classList.remove('customer-text-field__input--invalid');
        input.removeAttribute('aria-invalid');
        // Clear as soon as THIS field is corrected. #hideErrorSummaryIfValid below
        // waits for every required field to be valid, which was fine when the only
        // way to see a message was submitting a fully populated form. Blur
        // validation surfaces messages while the rest of the form is still blank,
        // and a message must not outlive the field it describes.
        if (input === this.#errorInput && this.#errorSummary) {
          this.#errorSummary.hidden = true;
          this.#errorInput = null;
        }
        this.#hideErrorSummaryIfValid();
      }
      this.#evaluate();
    };

    #handleInvalid = (event) => {
      event.preventDefault?.();
      const input = /** @type {HTMLInputElement} */ (event.currentTarget);
      input.classList.add('customer-text-field__input--invalid');
      input.setAttribute('aria-invalid', 'true');
      this.#showErrorForInput(input);
    };

    /**
     * Validate a field as the shopper leaves it, rather than making them submit to
     * find out. The `invalid` event cannot do this on its own: it only fires when
     * constraint validation runs, which is submit or an explicit checkValidity()
     * call — never a focus change.
     */
    #handleRequiredBlur = (event) => {
      const input = /** @type {HTMLInputElement} */ (event.currentTarget);

      // An empty field is not an error yet. Tabbing through a form the shopper has
      // not filled in must not light up every field behind them with "required" —
      // that message belongs to submit, which already reports it. Once there IS
      // content, a malformed email is worth flagging straight away.
      if (input.value.trim().length === 0) return;
      // Not `input.validity.valid` — that accepts a TLD-less address like
      // john@john. See isAccountInputValid.
      if (isAccountInputValid(input)) return;

      input.classList.add('customer-text-field__input--invalid');
      input.setAttribute('aria-invalid', 'true');

      // Crucially, no focus move. #showErrorForInput pulls focus to the summary so
      // a submit-time error is announced, but doing that on blur would yank focus
      // out of whatever the shopper just tabbed into and make the form impossible
      // to move through. role="alert" on the summary announces it either way.
      this.#showErrorForInput(input, { moveFocus: false });
    };

    #handleSubmit = (event) => {
      // The browser's own constraint validation has already run and passed by the
      // time this fires, so this is the only thing standing between a natively-valid
      // but TLD-less email and a round trip to Shopify.
      const firstInvalidInput = this.#requiredInputs.find((input) => !isAccountInputValid(input));
      if (!firstInvalidInput) return;

      event.preventDefault();
      this.#handleInvalid({
        currentTarget: firstInvalidInput,
        preventDefault() {},
      });
    };

    #showErrorForInput(input, { moveFocus = true } = {}) {
      if (!this.#errorSummary || !this.#errorText) return;

      // Covers the path input alone cannot: the email is echoed back after a
      // rejected submission, so blurring the field without touching it would show
      // the client message beneath the server one.
      if (input.type === 'email') this.#dismissServerEmailError();

      this.#errorInput = input;

      this.#errorText.textContent = getAccountInputErrorMessage(input, this.dataset);
      this.#errorSummary.hidden = false;

      if (!moveFocus) return;

      requestAnimationFrame(() => {
        this.#errorSummary?.focus({ preventScroll: true });
        this.#errorSummary?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    }

    #hideErrorSummaryIfValid() {
      if (!this.#errorSummary) return;
      const fieldsOk = this.#requiredInputs.every((input) => isAccountInputValid(input));
      if (fieldsOk) this.#errorSummary.hidden = true;
    }

    #evaluate = () => {
      const fieldsOk = this.#requiredInputs.every((input) => input.value.trim().length > 0);
      if (this.#submit) {
        const disabled = !(fieldsOk && this.#passwordAllValid);
        this.#submit.disabled = disabled;
        this.#submit.setAttribute('aria-disabled', String(disabled));
      }
    };
  }

  customElements.define('customer-register-form', CustomerRegisterForm);
}

// ---------------------------------------------------------------------------
// <customer-login-form>
// Manages two panels: sign-in (#customer-login-panel) and recover (#recover-panel).
// Buttons with data-customer-toggle="recover" or "signin" switch panels.
// On load, checks window.location.hash to deep-link to the recover panel.
// ---------------------------------------------------------------------------
if (!customElements.get('customer-login-form')) {
  class CustomerLoginForm extends HTMLElement {
    connectedCallback() {
      this.#signinPanel = this.querySelector('[data-login-panel="signin"]');
      this.#recoverPanel = this.querySelector('[data-login-panel="recover"]');
      this.#forms = [...this.querySelectorAll('form')];
      this.#serverAuthenticationError = Boolean(
        this.#signinPanel?.querySelector('[data-account-error-onload]')
      );

      this.addEventListener('click', this.#handleClick);

      for (const form of this.#forms) {
        form.noValidate = true;
        form.setAttribute('novalidate', 'novalidate');

        form.addEventListener('submit', this.#handleSubmit);

        const inputs = [...form.querySelectorAll('input[required]')];
        this.#validatedInputs.push(...inputs);
        for (const input of inputs) {
          input.addEventListener('input', this.#handleValidatedInput);
          input.addEventListener('blur', this.#handleValidatedBlur);
        }
      }

      // Deep-link: if URL hash is #recover, show recover panel immediately.
      // The Liquid template renders recover with [hidden] by default (unless the
      // recover form was just posted/errored, handled server-side). JS removes
      // [hidden] here for hash-based deep links.
      if (window.location.hash === '#recover') {
        this.#showPanel('recover');
      }

      // Handle back/forward navigation that changes the hash without a page reload.
      window.addEventListener('hashchange', this.#handleHashChange);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#handleClick);
      window.removeEventListener('hashchange', this.#handleHashChange);

      for (const form of this.#forms) {
        form.removeEventListener('submit', this.#handleSubmit);
      }

      for (const input of this.#validatedInputs) {
        input.removeEventListener('input', this.#handleValidatedInput);
        input.removeEventListener('blur', this.#handleValidatedBlur);
      }
    }

    /** @type {Element | null} */
    #signinPanel = null;
    /** @type {Element | null} */
    #recoverPanel = null;
    /** @type {HTMLFormElement[]} */
    #forms = [];
    /** @type {HTMLInputElement[]} */
    #validatedInputs = [];
    /**
     * The input the visible summary is describing. One message is shown at a time,
     * so this is what decides when it may be cleared.
     * @type {HTMLInputElement | null}
     */
    #errorInput = null;
    /** A rejected Shopify login remains visible until the shopper submits again. */
    #serverAuthenticationError = false;

    #handleHashChange = () => {
      this.#showPanel(window.location.hash === '#recover' ? 'recover' : 'signin');
    };

    #handleValidatedInput = (event) => {
      const input = /** @type {HTMLInputElement} */ (event.currentTarget);

      // A server authentication failure describes the email/password pair, not
      // either field's client validity. Keep its message, red fields, and typed
      // values in place while the shopper edits and retries.
      if (this.#serverAuthenticationError && input.form?.closest?.('[data-login-panel="signin"]')) {
        return;
      }

      if (this.#isInputValid(input)) {
        this.#setInputInvalid(input, false);
        // Clear as soon as THIS field is fixed. #hideFormErrorIfValid waits for every
        // required field to be valid, so correcting the email while the password was
        // still blank left the old message on screen looking unresolved.
        if (input === this.#errorInput) {
          const summary = input.form?.querySelector('[data-account-error-summary]');
          if (summary) summary.hidden = true;
          this.#errorInput = null;
        }
        this.#hideFormErrorIfValid(input.form);
      }
    };

    #handleValidatedBlur = (event) => {
      const input = /** @type {HTMLInputElement} */ (event.currentTarget);

      // An untouched empty field is not an error yet. Without this, tabbing off a
      // bad email reported "enter a password" for a field the shopper had not
      // reached — the empty password satisfied #isInputValid's required check and
      // claimed the shared summary.
      if (input.value.trim().length === 0) return;
      if (this.#isInputValid(input)) return;

      // No focus move on blur. See #showFormError.
      this.#showFormError(input, { moveFocus: false });
    };

    /**
     * Gate submission on the same rules the blur check uses.
     *
     * These forms are marked novalidate, and until now nothing replaced the native
     * reporting that switched off — the sign-in and reset-password forms posted a
     * blank or malformed email straight to Shopify and leaned on the server round
     * trip to reject it. A failed check blocks the submit outright; nothing is sent.
     */
    #handleSubmit = (event) => {
      const form = /** @type {HTMLFormElement} */ (event.currentTarget);
      const inputs = /** @type {HTMLInputElement[]} */ ([...form.querySelectorAll('input[required]')]);
      const invalid = inputs.filter((input) => !this.#isInputValid(input));
      if (invalid.length === 0) return;

      event.preventDefault();

      // Highlight every offending field, so what is marked matches what was
      // actually checked, then report the first in document order — the summary
      // carries one message at a time, and that is where the shopper goes back to.
      for (const input of invalid) {
        this.#setInputInvalid(input, true);
      }

      // Submit is the case moving focus is meant for: the shopper asked for the
      // form to be checked and focus is on the button, not on a field, so there is
      // no next-field blur handler to race with.
      this.#showFormError(invalid[0]);
    };

    #showFormError(input, { moveFocus = true } = {}) {
      this.#setInputInvalid(input, true);

      const form = input.form;
      const summary = form?.querySelector('[data-account-error-summary]');
      const errorText = form?.querySelector('[data-account-error-text]');
      if (!summary || !errorText) return;

      const message = this.#getInputErrorMessage(input);

      errorText.textContent = message;
      summary.hidden = false;
      this.#errorInput = input;

      // Moving focus here is only safe when validation was triggered by an explicit
      // action such as submit. On blur it is actively harmful: focus has already
      // landed on the NEXT field, so pulling it to the summary fires that field's
      // blur handler, which then overwrites this very message with its own. That is
      // how a bad email ended up reported as a password problem. role="alert" on the
      // summary announces the message without needing focus.
      if (!moveFocus) return;

      requestAnimationFrame(() => {
        summary.focus({ preventScroll: true });
        summary.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    }

    #hideFormErrorIfValid(form) {
      if (!form) return;
      if (this.#serverAuthenticationError && form.closest?.('[data-login-panel="signin"]')) return;
      const allValid = [...form.querySelectorAll('input[required]')].every((input) =>
        this.#isInputValid(/** @type {HTMLInputElement} */ (input))
      );
      if (!allValid) return;

      const summary = form.querySelector('[data-account-error-summary]');
      if (summary) summary.hidden = true;
    }

    // Both of these were the originals; they now live at module scope so the
    // create-account form validates against the same definition. Kept as thin
    // wrappers because they read better at the call sites than the free functions.

    #isInputValid(input) {
      return isAccountInputValid(input);
    }

    #getInputErrorMessage(input) {
      return getAccountInputErrorMessage(input, this.dataset);
    }

    #setInputInvalid(input, invalid) {
      const invalidClass = input.classList.contains('customer-password-field__input')
        ? 'customer-password-field__input--invalid'
        : 'customer-text-field__input--invalid';

      input.classList.toggle(invalidClass, invalid);
      if (invalid) {
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.removeAttribute('aria-invalid');
      }
    }

    #handleClick = (event) => {
      const toggle = event.target?.closest('[data-customer-toggle]');
      if (!toggle) return;

      const panel = toggle.dataset.customerToggle;

      // Update the URL hash so the state is bookmarkable and survives reload.
      // Use replaceState to avoid polluting the browser history stack.
      const hash = panel === 'recover' ? '#recover' : '';
      history.replaceState(null, '', window.location.pathname + window.location.search + hash);

      this.#showPanel(panel);
    };

    /** @param {'signin' | 'recover'} panel */
    #showPanel(panel) {
      if (panel === 'recover') {
        this.#signinPanel?.setAttribute('hidden', '');
        this.#recoverPanel?.removeAttribute('hidden');
        this.#recoverPanel?.querySelector('input')?.focus();
      } else {
        this.#recoverPanel?.setAttribute('hidden', '');
        this.#signinPanel?.removeAttribute('hidden');
        this.#signinPanel?.querySelector('input')?.focus();
      }
    }
  }

  customElements.define('customer-login-form', CustomerLoginForm);
}

// ---------------------------------------------------------------------------
// <customer-privacy-toggle>
// Toggles aria-expanded on its button and shows/hides the body div.
// Reads data-label-collapsed / data-label-expanded to update button text.
// ---------------------------------------------------------------------------
if (!customElements.get('customer-privacy-toggle')) {
  class CustomerPrivacyToggle extends HTMLElement {
    connectedCallback() {
      this.#button = this.querySelector('button[aria-expanded]');
      this.#body = this.querySelector('.customer-privacy-notice__more-text');

      this.#button?.addEventListener('click', this.#handleClick);
    }

    disconnectedCallback() {
      this.#button?.removeEventListener('click', this.#handleClick);
    }

    /** @type {HTMLButtonElement | null} */
    #button = null;
    /** @type {HTMLElement | null} */
    #body = null;

    #handleClick = () => {
      const expanded = this.#button?.getAttribute('aria-expanded') === 'true';
      const next = !expanded;

      this.#button?.setAttribute('aria-expanded', String(next));

      if (this.#body) {
        this.#body.toggleAttribute('hidden', !next);
      }

      // Update label if provided
      if (this.#button) {
        const label = next
          ? this.#button.dataset.labelExpanded
          : this.#button.dataset.labelCollapsed;
        if (label) {
          const labelSpan = this.#button.querySelector('.customer-privacy-notice__toggle-label');
          if (labelSpan) labelSpan.textContent = label;
        }
      }
    };
  }

  customElements.define('customer-privacy-toggle', CustomerPrivacyToggle);
}

// ---------------------------------------------------------------------------
// Order history accordion toggle. Kept outside <summary> because the row also
// contains a reorder form/button, and nested interactive controls inside
// <summary> are invalid and difficult to control reliably.
// ---------------------------------------------------------------------------
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const toggle = target?.closest('[data-customer-order-toggle]');
  if (!toggle) return;

  const panelId = toggle.getAttribute('aria-controls');
  const panel = panelId ? document.getElementById(panelId) : null;
  if (!panel) return;

  const expanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!expanded));
  panel.toggleAttribute('hidden', expanded);
});

// ---------------------------------------------------------------------------
// Order details add-to-bag forms.
// Keep the server-posting form markup as a no-JS fallback, then use the same
// Ajax cart update contract as PDP/product-card buttons: update cart sections,
// notify the cart chip/drawer, and show the existing cart toast when available.
// ---------------------------------------------------------------------------
const getCustomerOrderCartSectionIds = () => {
  const sectionIds = new Set(
    [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
  );
  return [...sectionIds];
};

const CUSTOMER_ORDER_PROPERTY_KEYS = [
  '_item_id',
  '_item_category',
  '_item_category2',
  '_item_category3',
  '_item_brand',
  '_ontario_grown',
  '_craft',
  '_price_drop',
  '_new',
  '_item_list_name',
];

const CUSTOMER_ORDER_ID_DATA_KEYS = [
  'ocsItemName',
  'ocsItemVariant',
  'ocsPriceCents',
  'ocsCompareAtPriceCents',
  'ocsVariantStatus',
];

/** @typedef {HTMLElement & { name: string, value: string, dataset: DOMStringMap }} CustomerOrderControl */
/** @typedef {{ rawItem: Record<string, unknown>, submittedProperties: Record<string, string> }} CustomerOrderLine */
/** @typedef {{ mode: 'per-line'|'whole-order', lines: CustomerOrderLine[] }} CustomerOrderContract */

/**
 * @param {CustomerOrderControl[]} controls
 * @param {string} name
 * @returns {CustomerOrderControl|null}
 */
const getSingleCustomerOrderControl = (controls, name) => {
  const matches = controls.filter((control) => control?.name === name);
  const control = matches[0];
  if (matches.length !== 1 || !control || !('value' in control)) return null;
  return control;
};

/**
 * @param {CustomerOrderControl[]} controls
 * @param {number|null} index
 * @returns {CustomerOrderLine|null}
 */
const readCustomerOrderLine = (controls, index) => {
  const prefix = index === null ? '' : `items[${index}]`;
  const idName = index === null ? 'id' : `${prefix}[id]`;
  const quantityName = index === null ? 'quantity' : `${prefix}[quantity]`;
  const idInput = getSingleCustomerOrderControl(controls, idName);
  const quantityInput = getSingleCustomerOrderControl(controls, quantityName);
  if (!idInput || !quantityInput || !idInput.dataset) return null;
  if (!CUSTOMER_ORDER_ID_DATA_KEYS.every((key) => Object.prototype.hasOwnProperty.call(idInput.dataset, key))) {
    return null;
  }

  /** @type {Record<string, string>} */
  const submittedProperties = {};
  for (const key of CUSTOMER_ORDER_PROPERTY_KEYS) {
    const propertyName = index === null
      ? `properties[${key}]`
      : `${prefix}[properties][${key}]`;
    const propertyInput = getSingleCustomerOrderControl(controls, propertyName);
    if (!propertyInput) return null;
    submittedProperties[key] = String(propertyInput.value);
  }

  return {
    rawItem: {
      itemId: submittedProperties._item_id,
      itemName: idInput.dataset.ocsItemName,
      // Only the view_item builder reads this, and it rejects a missing value on
      // its own. Deliberately absent from CUSTOMER_ORDER_ID_DATA_KEYS: coupling
      // the add path to an attribute it never uses would let a Liquid omission
      // silently kill add_to_cart analytics as well.
      itemAvailability: idInput.dataset.ocsItemAvailability,
      brand: submittedProperties._item_brand,
      itemVariant: idInput.dataset.ocsItemVariant,
      category: submittedProperties._item_category,
      category2: submittedProperties._item_category2,
      category3: submittedProperties._item_category3,
      ontarioGrown: submittedProperties._ontario_grown,
      craft: submittedProperties._craft,
      priceCents: idInput.dataset.ocsPriceCents,
      compareAtCents: idInput.dataset.ocsCompareAtPriceCents,
      variantStatus: idInput.dataset.ocsVariantStatus,
      itemListName: submittedProperties._item_list_name,
      quantity: quantityInput.value,
    },
    submittedProperties,
  };
};

/**
 * @param {HTMLFormElement} form
 * @returns {CustomerOrderContract|null}
 */
const readCustomerOrderContract = (form) => {
  if (typeof form.querySelectorAll !== 'function') return null;
  const controls = /** @type {CustomerOrderControl[]} */ ([...form.querySelectorAll('[name]')]);
  const perLineIds = controls.filter((control) => control?.name === 'id');
  const indexedIds = [];
  for (const control of controls) {
    const match = control.name.match(/^items\[(\d+)\]\[id\]$/);
    if (match) indexedIds.push(Number(match[1]));
  }

  if (perLineIds.length === 1 && indexedIds.length === 0) {
    const line = readCustomerOrderLine(controls, null);
    return line ? { mode: 'per-line', lines: [line] } : null;
  }

  if (perLineIds.length !== 0 || indexedIds.length === 0) return null;
  const orderedIndexes = [...indexedIds].sort((left, right) => left - right);
  if (orderedIndexes.some((index, position) => index !== position)) return null;

  const lines = [];
  for (const index of orderedIndexes) {
    const line = readCustomerOrderLine(controls, index);
    if (!line) return null;
    lines.push(line);
  }
  return { mode: 'whole-order', lines };
};

/**
 * @param {HTMLFormElement} form
 * @param {FormData} formData
 * @returns {{ api: any, payload: any }}
 */
const prepareCustomerOrderAnalytics = (form, formData) => {
  const contract = readCustomerOrderContract(form);
  const api = /** @type {any} */ (window).OCSAnalyticsEvents;
  if (
    !contract ||
    !api ||
    typeof api.createAddToCartPayload !== 'function' ||
    typeof api.publishOcsAnalyticsEvent !== 'function' ||
    typeof api.setCartLineProperties !== 'function'
  ) {
    return { api: null, payload: null };
  }

  const rawItems = Object.freeze(
    contract.lines.map((line) => Object.freeze({ ...line.rawItem }))
  );
  const firstRawItem = rawItems[0];
  if (!firstRawItem) return { api, payload: null };
  const listsAreValid = rawItems.every((item) => item.itemListName === 'My Account');
  let payload = null;

  try {
    if (contract.mode === 'per-line') {
      for (const key of CUSTOMER_ORDER_PROPERTY_KEYS) {
        formData.delete(`properties[${key}]`);
      }
      const persistenceItem = listsAreValid
        ? firstRawItem
        : { ...firstRawItem, itemListName: undefined };
      api.setCartLineProperties(formData, persistenceItem);
    } else {
      for (let index = 0; index < rawItems.length; index += 1) {
        const rawItem = rawItems[index];
        if (!rawItem) continue;
        for (const key of CUSTOMER_ORDER_PROPERTY_KEYS) {
          formData.delete(`items[${index}][properties][${key}]`);
        }

        const temporaryFormData = new FormData();
        const persistenceItem = rawItem.itemListName === 'My Account'
          ? rawItem
          : { ...rawItem, itemListName: undefined };
        api.setCartLineProperties(temporaryFormData, persistenceItem);
        for (const key of CUSTOMER_ORDER_PROPERTY_KEYS) {
          const value = temporaryFormData.get(`properties[${key}]`);
          if (typeof value === 'string') {
            formData.set(`items[${index}][properties][${key}]`, value);
          }
        }
      }
    }

    // Defect G17. Production distinguished the two reorder paths by which form
    // was submitted: `.reorder-form--item` sent `single`, `.reorder-form--order`
    // sent `reorder` on every line. Horizon's equivalent distinction is already
    // in the contract, as its mode.
    payload = api.createAddToCartPayload(
      rawItems,
      'My Account',
      contract.mode === 'per-line' ? 'single' : 'reorder'
    );
  } catch (_) {
    return { api, payload: null };
  }

  if (!listsAreValid || !payload || payload.items.length !== contract.lines.length) {
    return { api, payload: null };
  }

  return { api, payload };
};

/**
 * Publish ocs:view_item when a shopper clicks through to a product from their
 * order history — defect G15.
 *
 * Production's view_item was never a page view: it was a click on any
 * `a[href*="/products/"]`, with `location` read off whichever container the link
 * sat in. Order history matched none of its containers, so production reported
 * these as `N/A`. We send `My Account`, which is the surface the merchant asked
 * to see and is already on the location whitelist.
 *
 * The line's own form is the authority for the metadata, exactly as the add
 * path uses it, so the two events on the same line always agree.
 */
document.addEventListener('click', (event) => {
  const target = /** @type {any} */ (event.target);
  if (!target || typeof target.closest !== 'function') return;

  // Production's own predicate. Locale-prefixed and collection-scoped product
  // URLs both still contain the segment.
  const link = target.closest('a[href*="/products/"]');
  if (!link) return;

  // The dashboard accordion nests the product link inside the line's own form;
  // the standalone order page puts them in different table cells. The line
  // marker is what the two surfaces have in common.
  const lineRoot = typeof link.closest === 'function' ? link.closest('[data-ocs-order-line]') : null;
  if (!lineRoot) return;

  const form = /** @type {HTMLFormElement|null} */ (
    typeof lineRoot.matches === 'function' && lineRoot.matches('[data-customer-order-add-form]')
      ? lineRoot
      : lineRoot.querySelector?.('[data-customer-order-add-form]') ?? null
  );
  // An unavailable line renders no reorder form, so it carries no metadata to
  // report. Production fired on the link regardless; we stay silent rather than
  // send a view with nothing in it.
  if (!form) return;

  const api = /** @type {any} */ (window).OCSAnalyticsEvents;
  if (
    !api ||
    typeof api.createViewItemPayload !== 'function' ||
    typeof api.publishOcsAnalyticsEvent !== 'function'
  ) {
    return;
  }

  try {
    const contract = readCustomerOrderContract(form);
    const line = contract?.lines[0];
    if (!line) return;

    const { rawItem, submittedProperties } = line;
    const payload = api.createViewItemPayload(
      {
        itemId: rawItem.itemId,
        itemName: rawItem.itemName,
        itemVariant: rawItem.itemVariant,
        brand: rawItem.brand,
        category: rawItem.category,
        category2: rawItem.category2,
        category3: rawItem.category3,
        priceCents: rawItem.priceCents,
        ontarioGrown: rawItem.ontarioGrown,
        craft: rawItem.craft,
        // The view builder wants the normalized badges, which Liquid already
        // rendered onto the line's own property inputs. Re-deriving them from
        // compare-at and variant status here would be a second computation that
        // could disagree with the one the add path validates against.
        priceDrop: submittedProperties._price_drop,
        new: submittedProperties._new,
        itemAvailability: rawItem.itemAvailability,
        itemListName: rawItem.itemListName,
      },
      'My Account'
    );
    if (!payload) return;

    api.publishOcsAnalyticsEvent('ocs:view_item', payload);
  } catch (_) {}
});

document.addEventListener('submit', async (event) => {
  const eventTarget = /** @type {any} */ (event.target);
  const form = /** @type {HTMLFormElement|null} */ (
    eventTarget &&
    typeof eventTarget.matches === 'function' &&
    eventTarget.matches('[data-customer-order-add-form]')
      ? eventTarget
      : null
  );
  if (!form) return;

  const button = /** @type {HTMLButtonElement|null} */ (
    typeof form.querySelector === 'function' ? form.querySelector('[type="submit"]') : null
  );
  if (!button || button.disabled) return;

  const routes = /** @type {any} */ (window.Shopify)?.routes;
  const root = typeof routes?.root === 'string' && routes.root.length > 0 ? routes.root : null;
  const themeRoutes = typeof Theme === 'object' && Theme !== null ? Theme.routes : null;
  const configuredCartAddUrl = themeRoutes?.cart_add_url;
  const cartAddUrl =
    typeof configuredCartAddUrl === 'string' && configuredCartAddUrl.trim().length > 0
      ? configuredCartAddUrl
      : null;
  if (!cartAddUrl) return;

  event.preventDefault();

  const status = form.querySelector('[data-customer-order-add-status]');
  if (status && 'textContent' in status && 'hidden' in status) {
    status.textContent = '';
    status.hidden = true;
  }

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    const formData = new FormData(form);
    const variantId = String(formData.get('id') || formData.get('items[0][id]') || '');
    if (!variantId) throw new Error('Missing variant ID');
    const analytics = prepareCustomerOrderAnalytics(form, formData);

    const sectionIds = getCustomerOrderCartSectionIds();
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const response = await fetch(cartAddUrl, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.message || data?.description || 'Failed to add to cart');
    }

    if (analytics.api && analytics.payload) {
      try {
        analytics.api.publishOcsAnalyticsEvent('ocs:add_to_cart', analytics.payload);
      } catch (_) {}
    }

    let lineItem = data;
    if (!lineItem || typeof lineItem.price !== 'number' || !lineItem.title) {
      try {
        if (!root) throw new Error('Missing localized storefront root');
        const cartResponse = await fetch(`${root.endsWith('/') ? root : `${root}/`}cart.js`);
        const cart = await cartResponse.json();
        lineItem = cart.items?.find((item) => String(item.variant_id) === variantId) || lineItem;
      } catch (_) {
        // Keep the cart/add.js response when the UI completion request fails.
      }
    }

    const indexedItemCount = [...formData]
      .filter(([name]) => /^items\[\d+\]\[quantity\]$/.test(name))
      .reduce((total, [, quantity]) => total + (Number(quantity) || 0), 0);
    const itemCount = Number(formData.get('quantity')) || indexedItemCount || 1;
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: lineItem,
          sourceId: `customer-order-${variantId}`,
          data: {
            source: 'product-form-component',
            itemCount,
            variantId,
            sections: data?.sections || {},
          },
        },
      })
    );

    let toastHandled = false;
    const toast = document.querySelector('ocs-cart-toast-component');
    if (
      toast &&
      typeof toast.showForItem === 'function' &&
      lineItem &&
      typeof lineItem.price === 'number' &&
      lineItem.title
    ) {
      toast.showForItem(lineItem);
      toastHandled = true;
    }

    if (!toastHandled) {
      const drawer = document.querySelector('ocs-cart-drawer-component, cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    }

  } catch (error) {
    console.error('[OCS] Order details add-to-cart error:', error);
    if (status && 'textContent' in status && 'hidden' in status) {
      status.textContent = form.dataset.addErrorLabel || 'Failed to add to cart';
      status.hidden = false;
    }
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
});

// ---------------------------------------------------------------------------
// Whole-order ("Reorder Order") forms.
// Adds every reorderable line item from a past order in a single cart/add.js
// call, refreshes the cart chip/drawer, and shows the multi-item cart toast —
// instead of the native POST that redirects to the cart page. The native form
// action stays as a no-JS fallback.
// ---------------------------------------------------------------------------

/**
 * Reconstruct a [{ id, quantity, properties }] list from a form's indexed
 * `items[N][id]` / `items[N][quantity]` / `items[N][properties][key]` fields.
 * Both whole-order forms emit indexed names so FormData keys stay unambiguous.
 */
const parseReorderItems = (form) => {
  const formData = new FormData(form);
  const byIndex = new Map();

  for (const [key, value] of formData.entries()) {
    const field = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
    const property = key.match(/^items\[(\d+)\]\[properties\]\[(.+)\]$/);

    if (field) {
      const [, index, name] = field;
      const item = byIndex.get(index) || {};
      item[name] = value;
      byIndex.set(index, item);
    } else if (property) {
      const [, index, name] = property;
      const item = byIndex.get(index) || {};
      item.properties = item.properties || {};
      item.properties[name] = value;
      byIndex.set(index, item);
    }
  }

  return [...byIndex.keys()]
    .sort((a, b) => Number(a) - Number(b))
    .map((index) => {
      const raw = byIndex.get(index);
      const item = { id: Number(raw.id), quantity: Number(raw.quantity) || 1 };
      if (raw.properties) item.properties = raw.properties;
      return item;
    })
    .filter((item) => item.id);
};

document.addEventListener('submit', async (event) => {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (!form?.matches('[data-customer-order-reorder-form]')) return;

  const button = form.querySelector('[type="submit"]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const items = parseReorderItems(form);
  // Nothing parsed — let the native submit proceed (no-JS fallback contract).
  if (!items.length) return;

  event.preventDefault();

  const status = form.querySelector('[data-customer-order-add-status]');
  if (status instanceof HTMLElement) {
    status.textContent = '';
    status.hidden = true;
  }

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    const root = window.Shopify?.routes?.root || '/';
    const base = root.endsWith('/') ? root : `${root}/`;

    const body = { items };
    const sectionIds = getCustomerOrderCartSectionIds();
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(`${base}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.message || data?.description || 'Failed to add to cart');
    }

    // Analytics parity with the per-line add path: publish the enriched
    // ocs:add_to_cart for the whole-order reorder. A throwaway FormData is used
    // so the analytics normalization never disturbs the JSON add above.
    try {
      const analytics = prepareCustomerOrderAnalytics(form, new FormData(form));
      if (analytics.api && analytics.payload) {
        analytics.api.publishOcsAnalyticsEvent('ocs:add_to_cart', analytics.payload);
      }
    } catch (_) {}

    const addedItems = Array.isArray(data.items) ? data.items : [];
    const itemCount = items.reduce((sum, item) => sum + (item.quantity || 1), 0);

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          sourceId: 'customer-order-reorder',
          data: {
            source: 'product-form-component',
            itemCount,
            sections: data.sections || {},
          },
        },
      })
    );

    let toastHandled = false;
    const toast = document.querySelector('ocs-cart-toast-component');
    if (toast && typeof toast.showForItems === 'function' && addedItems.length) {
      toast.showForItems(addedItems);
      toastHandled = true;
    }

    if (!toastHandled) {
      const drawer = document.querySelector('ocs-cart-drawer-component, cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    }
  } catch (error) {
    console.error('[OCS] Order reorder add-to-cart error:', error);
    if (status instanceof HTMLElement) {
      status.textContent = form.dataset.addErrorLabel || 'Failed to add to cart';
      status.hidden = false;
    }
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
});

// ---------------------------------------------------------------------------
// <customer-address-form>
// Formats address phone/postal fields. If a province <select data-province>
// exists, populates it with CA_PROVINCES and pre-selects the saved province.
// ---------------------------------------------------------------------------
if (!customElements.get('customer-address-form')) {
  class CustomerAddressForm extends HTMLElement {
    connectedCallback() {
      this.#provinceSelect = this.querySelector('select[data-province]');

      if (this.#provinceSelect) {
        // Only populate if there are no real province options yet — a single empty
        // placeholder option (for floating-label state) is treated as "empty".
        const hasRealOptions = Array.from(this.#provinceSelect.options).some(
          (opt) => opt.value !== ''
        );
        if (!hasRealOptions) {
          this.#populateProvinces();
        }

        this.#preselectProvince();
      }

      this.#phoneInput = this.querySelector('.js_address_phone');
      if (this.#phoneInput) {
        this.#phoneInput.addEventListener('input', this.#formatPhone);
        // Format pre-filled value (edit form may have a saved phone)
        this.#formatPhoneValue(this.#phoneInput);
      }

      this.#zipInput = this.querySelector('.js_address_zip');
      if (this.#zipInput) {
        this.#zipInput.addEventListener('input', this.#formatZip);
        // Format pre-filled value (edit form may have a saved zip)
        this.#formatZipValue(this.#zipInput);
      }
    }

    disconnectedCallback() {
      this.#phoneInput?.removeEventListener('input', this.#formatPhone);
      this.#zipInput?.removeEventListener('input', this.#formatZip);
    }

    /** @type {HTMLSelectElement | null} */
    #provinceSelect = null;

    /** @type {HTMLInputElement | null} */
    #phoneInput = null;

    /** @type {HTMLInputElement | null} */
    #zipInput = null;

    #populateProvinces() {
      if (!this.#provinceSelect) return;

      for (const province of CA_PROVINCES) {
        const option = document.createElement('option');
        option.value = province.code;
        option.textContent = province.label;
        this.#provinceSelect.appendChild(option);
      }
    }

    #preselectProvince() {
      if (!this.#provinceSelect) return;

      // data-default-province is set by Liquid for edit forms (the saved province code)
      const defaultProvince = this.#provinceSelect.dataset.defaultProvince || 'ON';
      this.#provinceSelect.value = defaultProvince;
    }

    #formatPhone = (event) => {
      this.#formatPhoneValue(event.target);
    };

    #formatPhoneValue = (input) => {
      if (!input) return;
      const digits = (input.value || '').replace(/\D/g, '').slice(0, 10);
      let formatted = digits;
      if (digits.length > 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }
      if (input.value !== formatted) {
        const prevLen = input.value.length;
        const prevCaret = input.selectionStart ?? formatted.length;
        input.value = formatted;
        // Best-effort caret restore: keep caret at end if user was typing at the end;
        // otherwise shift by the diff in length.
        const newCaret =
          prevCaret >= prevLen ? formatted.length : prevCaret + (formatted.length - prevLen);
        try {
          input.setSelectionRange(newCaret, newCaret);
        } catch (_) {
          /* some input types disallow setSelectionRange — ignore */
        }
      }
    };

    #formatZip = (event) => {
      this.#formatZipValue(event.target);
    };

    #formatZipValue = (input) => {
      if (!input) return;
      const alnum = (input.value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6);
      let formatted = alnum;
      if (alnum.length > 3) {
        formatted = `${alnum.slice(0, 3)} ${alnum.slice(3)}`;
      }
      if (input.value !== formatted) {
        const prevLen = input.value.length;
        const prevCaret = input.selectionStart ?? formatted.length;
        input.value = formatted;
        const newCaret =
          prevCaret >= prevLen ? formatted.length : prevCaret + (formatted.length - prevLen);
        try {
          input.setSelectionRange(newCaret, newCaret);
        } catch (_) {
          /* ignore */
        }
      }
    };
  }

  customElements.define('customer-address-form', CustomerAddressForm);
}

window.Shopify = window.Shopify || {};

if (typeof window.Shopify.postLink !== 'function') {
  window.Shopify.postLink = function (path, options) {
    options = options || {};
    const method = options.method || 'post';
    const params = options.parameters || {};

    const form = document.createElement('form');
    form.setAttribute('method', method);
    form.setAttribute('action', path);

    Object.keys(params).forEach((key) => {
      const hiddenField = document.createElement('input');
      hiddenField.setAttribute('type', 'hidden');
      hiddenField.setAttribute('name', key);
      hiddenField.setAttribute('value', params[key]);
      form.appendChild(hiddenField);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };
}

// ---------------------------------------------------------------------------
// <customer-account-dialogs>
// Document-level singleton that delegates clicks on:
//   [data-customer-dialog-open="<id>"]  → finds <dialog-component id="<id>"> → .showDialog()
//   [data-customer-dialog-close="<id>"] → same → .closeDialog()
// Appended to document.body once on DOMContentLoaded.
// ---------------------------------------------------------------------------
if (!customElements.get('customer-account-dialogs')) {
  class CustomerAccountDialogs extends HTMLElement {
    /** @type {HTMLElement | null} */
    #deleteDialogLastTrigger = null;

    connectedCallback() {
      document.addEventListener('click', this.#handleClick, { capture: true });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.#handleClick, { capture: true });
    }

    #handleClick = (event) => {
      const openTrigger = event.target?.closest('[data-customer-dialog-open]');
      const closeTrigger = event.target?.closest('[data-customer-dialog-close]');
      const deleteConfirmTrigger = event.target?.closest('[data-customer-address-delete-confirm]');
      const deleteTrigger = event.target?.closest('.js_delete_address[data-confirm-message][data-target]');

      if (openTrigger) {
        const id = openTrigger.dataset.customerDialogOpen;
        const dialog = id ? document.getElementById(id) : null;
        if (dialog && typeof dialog.showDialog === 'function') {
          event.preventDefault();
          dialog.showDialog();
        }
        return;
      }

      if (closeTrigger) {
        const id = closeTrigger.dataset.customerDialogClose;
        const dialog = id ? document.getElementById(id) : null;
        if (dialog && typeof dialog.closeDialog === 'function') {
          event.preventDefault();
          dialog.closeDialog();
        }
        return;
      }

      if (deleteConfirmTrigger) {
        event.preventDefault();

        const dialog = document.getElementById('customer-delete-address');
        const deleteUrl = dialog?.dataset.deleteUrl;
        if (!deleteUrl) return;

        deleteConfirmTrigger.setAttribute('disabled', '');
        window.Shopify.postLink(deleteUrl, {
          parameters: { _method: 'delete' },
        });
        return;
      }

      if (deleteTrigger) {
        event.preventDefault();

        const deleteUrl = deleteTrigger.dataset.target;
        if (!deleteUrl) return;

        const message =
          deleteTrigger.getAttribute('data-confirm-message') ?? 'Are you sure you want to delete this address?';
        const dialog = document.getElementById('customer-delete-address');

        if (dialog && typeof dialog.showDialog === 'function') {
          dialog.dataset.deleteUrl = deleteUrl;
          const messageEl = dialog.querySelector('[data-customer-address-delete-message]');
          if (messageEl) messageEl.textContent = message;

          this.#deleteDialogLastTrigger = deleteTrigger;
          dialog.addEventListener('dialog:close', () => {
            this.#deleteDialogLastTrigger?.focus();
            this.#deleteDialogLastTrigger = null;
            delete dialog.dataset.deleteUrl;
          }, { once: true });
          dialog.showDialog();
          return;
        }

        console.warn('[customer-account] Delete confirmation dialog is missing. Address was not deleted.');
      }
    };
  }

  customElements.define('customer-account-dialogs', CustomerAccountDialogs);

  // Bootstrap the singleton once the DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
    if (!document.querySelector('customer-account-dialogs')) {
      const singleton = document.createElement('customer-account-dialogs');
      document.body.appendChild(singleton);
    }
  });
}


// ---------------------------------------------------------------------------
// Server-rendered form errors — load-time announcement
//
// Client-side validation errors announce themselves: #showFormError populates
// the summary and unhides it, and a role="alert" region does announce a
// mutation made after assistive tech has registered it.
//
// A server-rendered error is the opposite case and needs its own path. Sign-in
// failures, "this email already has an account", and the like come back as a
// fresh page with the message already in the HTML. That is not a mutation, so
// the live region never fires — the message is visible and silent. Two things
// have to happen explicitly instead:
//
//   1. Prefix the document title. On a page load the title is the first thing
//      announced, and it is the only channel with no focus race and no live
//      region timing to lose. Runs even if the focus call below fails. Sighted
//      users see it too, in the tab — that is intended, not a side effect.
//   2. Move focus to the summary, so the message itself is read out. The
//      summary rather than the first invalid field: focusing the field skips
//      every other error, and for the account-already-exists notice the useful
//      content IS the summary, which holds the sign-in / reset links.
//
// Top-level, not on DOMContentLoaded: every account section loads this file as
// type="module", so it is deferred — the DOM is fully parsed by the time this
// runs, but DOMContentLoaded and load have not fired yet. That is the earliest
// hook available without an inline script, and comfortably ahead of the title
// being announced.
// ---------------------------------------------------------------------------
{
  const candidates = [...document.querySelectorAll('[data-account-error-onload]')];

  // Register can emit two of these at once (an account-level block above the
  // title plus an email-specific one by the field), and the login page always
  // renders both its sign-in and recover containers with one of them hidden.
  // Prefer the first that is actually visible; fall back to the first in
  // document order if the visibility check is inconclusive.
  const summary =
    candidates.find((element) => !element.hidden && element.getClientRects().length > 0) ?? candidates[0];

  if (summary) {
    const prefix = summary.dataset.errorTitlePrefix?.trim();
    if (prefix && !document.title.startsWith(prefix)) {
      document.title = `${prefix} ${document.title}`;
    }

    // rAF for the same reason #showFormError uses one: focus set during script
    // evaluation can be lost to the browser's own post-load focus handling.
    // preventScroll + explicit scrollIntoView so the box lands centered rather
    // than jammed against the viewport edge.
    requestAnimationFrame(() => {
      summary.focus({ preventScroll: true });
      summary.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }
}
